import { isEmpty, sortBy } from 'lodash-es';
import { Branch } from '../../../dorian-shared/types/branch/Branch';
import { StepTypes } from '../../../dorian-shared/types/branch/BranchStep';
import { logger } from '../../loggerService/loggerService';
import { COMMAND_KEY_PATTERN } from '../types/episodeTextImportCommandKeyValues';
import { stripCharacterMemoryPrefix } from '../utils/characterMemoryPrefix';
import {
  EpisodeTextImportCommandKey,
  EpisodeTextImportCommands,
  TEpisodeTextImportCommands,
  TEpisodeTextImportCommandValue,
} from '../types/episodeTextImportCommandTypes';
import { EpisodeTextColumnKey, EpisodeTextEntityType } from '../types/episodeTextImportTypes';
import { EpisodeTextImport } from './EpisodeTextImport';
import { EpisodeTextImportNode } from './EpisodeTextImportNode';
import { EpisodeTextImportStep } from './EpisodeTextImportStep';

export class EpisodeTextImportHelper {
  public static getRowType(importRow: string[], prevType?: EpisodeTextEntityType): EpisodeTextEntityType {
    const nodeOrSpeaker = importRow[EpisodeTextColumnKey.NodeOrSpeaker] ?? '';
    const nodeOrSpeakerCleared = EpisodeTextImportHelper.removeAllCommandsFromString(nodeOrSpeaker);
    if (nodeOrSpeaker.startsWith('//')) {
      return EpisodeTextEntityType.Skip;
    }
    const text = importRow[EpisodeTextColumnKey.Text] ?? '';
    const textCleared = EpisodeTextImportHelper.removeAllCommandsFromString(text);
    const description = importRow[EpisodeTextColumnKey.Description] ?? '';

    if (!nodeOrSpeaker && !text && !description) {
      return EpisodeTextEntityType.EmptyStep;
    }

    let commandsIn: TEpisodeTextImportCommands = new Map();

    commandsIn = EpisodeTextImportHelper.parseCommands(EpisodeTextColumnKey.NodeOrSpeaker, nodeOrSpeaker, commandsIn);
    commandsIn = EpisodeTextImportHelper.parseCommands(EpisodeTextColumnKey.Text, text, commandsIn);
    commandsIn = EpisodeTextImportHelper.parseCommands(EpisodeTextColumnKey.Description, description, commandsIn);

    const commands = EpisodeTextImportHelper.convertCommandsToRecordType(commandsIn);

    const isEnding = commands.ending.has(EpisodeTextColumnKey.NodeOrSpeaker)
    || commands.ending.has(EpisodeTextColumnKey.Text)
    || commands.ending.has(EpisodeTextColumnKey.Description);
    if (isEnding) {
      return EpisodeTextEntityType.Ending;
    }

    const isChoiceCommand = commands.choice.has(EpisodeTextColumnKey.Text) || commands.choice.has(EpisodeTextColumnKey.Description);
    if (isChoiceCommand) {
      return EpisodeTextEntityType.Choice;
    }

    const isChat = commands.chat.get(EpisodeTextColumnKey.Description);
    if (isChat && !isEmpty(nodeOrSpeakerCleared) && !isEmpty(textCleared)) {
      return EpisodeTextEntityType.Chat;
    }

    /*
      The reaction must be detected before the thinking (as expression),
      since the reaction can be used with the thinking command
    */
    const isReact = commands.react.get(EpisodeTextColumnKey.Description);
    if (isReact && !isEmpty(nodeOrSpeakerCleared)) {
      return EpisodeTextEntityType.Reaction;
    }

    const isThink = commands.thoughtful.get(EpisodeTextColumnKey.Description);
    if (isThink && !isEmpty(nodeOrSpeakerCleared) && !isEmpty(textCleared)) {
      return EpisodeTextEntityType.Thinking;
    }

    const isGotoFromNode = commands.goto.get(EpisodeTextColumnKey.Text);
    if (isGotoFromNode && isEmpty(nodeOrSpeakerCleared)) {
      return EpisodeTextEntityType.GotoFromNode;
    }

    const isNode = !isEmpty(nodeOrSpeakerCleared) && isEmpty(textCleared);
    if (isNode) {
      return EpisodeTextEntityType.Node;
    }

    if (prevType === EpisodeTextEntityType.Choice || prevType === EpisodeTextEntityType.Answer) {
      return EpisodeTextEntityType.Answer;
    }

    if (prevType === EpisodeTextEntityType.CheckConditionList || prevType === EpisodeTextEntityType.CheckCondition) {
      // Unconditionally treat as CheckCondition when following a CheckConditionList/CheckCondition,
      // same as Answer follows Choice unconditionally. Invalid operators are caught by validation,
      // not by the parser — prevents type from flipping to Narration on typos.
      if (isEmpty(nodeOrSpeakerCleared)) {
        return EpisodeTextEntityType.CheckCondition;
      }
    }

    const hasRemember = commands.remember.has(EpisodeTextColumnKey.Text)
      || commands.remember.has(EpisodeTextColumnKey.Description);
    const isRemember = hasRemember && isEmpty(nodeOrSpeakerCleared);
    if (isRemember) {
      return EpisodeTextEntityType.Remember;
    }

    const hasRandom = commands.random.has(EpisodeTextColumnKey.Text)
      || commands.random.has(EpisodeTextColumnKey.Description);
    const isRandom = hasRandom && isEmpty(nodeOrSpeakerCleared);
    if (isRandom) {
      return EpisodeTextEntityType.Random;
    }

    const isCheck = commands.check.has(EpisodeTextColumnKey.Text)
      || commands.check.has(EpisodeTextColumnKey.Description);
    if (isCheck) {
      // Determine if this is a condition list check
      const checkType = EpisodeTextImportHelper.getCheckEntityTypes(commandsIn);
      if (checkType === 'conditionList') {
        return EpisodeTextEntityType.CheckConditionList;
      }
      return EpisodeTextEntityType.Check;
    }

    const isVideo = commands.video.has(EpisodeTextColumnKey.Text) || commands.video.has(EpisodeTextColumnKey.Description);
    if (isVideo) {
      return EpisodeTextEntityType.Video;
    }

    const isAchieve = commands.achieve.has(EpisodeTextColumnKey.Text) || commands.achieve.has(EpisodeTextColumnKey.Description);
    if (isAchieve) {
      return EpisodeTextEntityType.Achieve;
    }

    const isDialogue = !isEmpty(nodeOrSpeakerCleared) && !isEmpty(textCleared);
    if (isDialogue) {
      return EpisodeTextEntityType.Dialogue;
    }

    const isNarration = isEmpty(nodeOrSpeakerCleared) && !isEmpty(textCleared);
    if (isNarration) {
      return EpisodeTextEntityType.Narration;
    }

    return EpisodeTextEntityType.Node;
  }

  public static parseTextToEpisodeText(text: string): EpisodeTextImport | undefined {
    const episodeTextImport = new EpisodeTextImport();
    const rows = text.split('\n');
    const importRows = rows.map((row) => row.split('\t'));
    const firstIndex = importRows.findIndex((row) => EpisodeTextImportHelper.getRowType(row) === EpisodeTextEntityType.Node);
    if (firstIndex === -1) {
      return undefined;
    }

    let rowType: EpisodeTextEntityType = EpisodeTextEntityType.Skip;

    for (let rowIndex = firstIndex; rowIndex < importRows.length; rowIndex++) {
      const importRow = importRows[rowIndex] ?? [];

      rowType = EpisodeTextImportHelper.getRowType(importRow, rowType);

      switch (rowType) {
        case EpisodeTextEntityType.EmptyStep:
        case EpisodeTextEntityType.Skip:
          logger.log('[EpisodeTextImportUtils] Skip row', importRow);
          break;
        case EpisodeTextEntityType.Node: {
          const node = new EpisodeTextImportNode(importRow);
          episodeTextImport.addNode(node);
          break;
        }
        case EpisodeTextEntityType.CheckCondition: {
          // Add condition to the last CheckConditionList step
          const step = new EpisodeTextImportStep(importRow, rowType);
          episodeTextImport.addConditionToLastConditionListCheck(step);
          break;
        }
        default: {
          const step = new EpisodeTextImportStep(importRow, rowType);
          episodeTextImport.addStep(step);
          break;
        }
      }
    }
    episodeTextImport.moveEndingStepsToLastNode();
    // Assign stable deterministic keys to GotoFromNode and CheckCondition rows
    // so that snapshot/dirty-tracking and revertNode work correctly across reparsing.
    episodeTextImport.assignDeterministicIds();
    return episodeTextImport;
  }

  public static parseCommands(
    columnKey: EpisodeTextColumnKey,
    text: string,
    commands: TEpisodeTextImportCommands = new Map(),
  ):
    TEpisodeTextImportCommands {
    // Fast path: no commands possible if text contains no '#'
    if (!text.includes('#')) return commands.size > 0 ? new Map(commands) : commands;

    const newCommands = new Map(commands);
    Object.entries(EpisodeTextImportCommands).forEach(([key, value]) => {
      for (let valueIndex = 0; valueIndex < value.length; valueIndex++) {
        const command = value[valueIndex];

        const match = text.match(command.pattern);
        if (match) {
          const commandValue: TEpisodeTextImportCommandValue = newCommands.get(key as EpisodeTextImportCommandKey) ?? new Map();
          commandValue.set(columnKey, match);
          newCommands.set(key as EpisodeTextImportCommandKey, commandValue);
          break;
        }
      }
    });
    return newCommands;
  }

  public static getLowestYInNodes(nodes: Branch[]) {
    return nodes.reduce((acc, node) => {
      if (node.y > acc) {
        return node.y;
      }
      return acc;
    }, 0);
  }

  public static getStepTypeIdByEntityType(entityType: EpisodeTextEntityType | undefined): number | undefined {
    switch (entityType) {
      case EpisodeTextEntityType.Dialogue:
        return StepTypes.Dialogue;
      case EpisodeTextEntityType.Narration:
        return StepTypes.Narrator;
      case EpisodeTextEntityType.Choice:
        return StepTypes.Choice;
      case EpisodeTextEntityType.Chat:
        return StepTypes.Texting;
      case EpisodeTextEntityType.Thinking:
        return StepTypes.Thinking;
      case EpisodeTextEntityType.Reaction:
        return StepTypes.Reaction;
      case EpisodeTextEntityType.Remember:
        return StepTypes.Remember;
      case EpisodeTextEntityType.Random:
        return StepTypes.Random;
      case EpisodeTextEntityType.Check:
      case EpisodeTextEntityType.CheckConditionList:
        return StepTypes.Check;
      case EpisodeTextEntityType.Ending:
        return StepTypes.Ending;
      case EpisodeTextEntityType.Video:
        return StepTypes.Video;
      case EpisodeTextEntityType.Achieve:
        return StepTypes.Achieve;
      default:
        return undefined;
    }
  }

  public static isNameExists(name: string, existingNames: string[], ifEmpty = true): boolean {
    if (isEmpty(name)) return ifEmpty;
    return existingNames.some((el) => el?.toLocaleLowerCase().trim() === name.toLocaleLowerCase().trim());
  }

  public static removeAllCommandsFromString(text: string) {
    // Fast path: no commands possible if text contains no '#'
    if (!text.includes('#')) return text.trim();

    let newText = text;
    Object.values(EpisodeTextImportCommands).forEach((commandType) => {
      commandType.forEach((command) => {
        newText = newText.replace(command.pattern, '');
      });
    });
    return newText.trim();
  }

  private static _calculateSubtreeWidths = (
    tree: { [key: string]: number[] },
    startNode: number,
    visited: Set<number> = new Set(),
  ): { [key: number]: number } => {
    const widths: { [key: number]: number } = {};

    function dfs(node: number): number {
      if (visited.has(node)) return 0;
      visited.add(node);

      const children = tree[node] ?? [];
      if (children.length === 0) {
        widths[node] = 1;
        return 1;
      }

      let totalWidth = 0;
      children.forEach((child) => {
        totalWidth += dfs(child);
      });

      widths[node] = Math.max(totalWidth, 1);
      return widths[node];
    }

    dfs(startNode);
    return widths;
  };

  public static assignPositions = (
    tree: { [key: string]: number[] },
    startNode: number,
    initialPosition: { x: number; y: number } = { x: 0, y: 0 },
  ) => {
    const offsetX = 200;
    const offsetY = 350;
    const positions: { [key: string]: { x: number; y: number } } = {};
    const widths = this._calculateSubtreeWidths(tree, startNode);
    const positioned = new Set<number>();

    function layout(node: number, depth: number, slotStartX: number, slotWidth: number) {
      if (positioned.has(node)) return;
      positioned.add(node);

      const x = slotStartX + ((slotWidth - 1) * offsetX) / 2;
      const y = initialPosition.y + depth * offsetY;
      positions[node] = { x, y };

      const children = (tree[node] ?? []).filter((child) => !positioned.has(child));
      let currentX = slotStartX;
      children.forEach((child) => {
        const childWidth = widths[child] || 1;
        layout(child, depth + 1, currentX, childWidth);
        currentX += childWidth * offsetX;
      });
    }

    const rootWidth = widths[startNode] || 1;
    const treeStartX = initialPosition.x - ((rootWidth - 1) * offsetX) / 2;
    layout(startNode, 0, treeStartX, rootWidth);
    return positions;
  };

  public static convertCommandsToRecordType(commands: TEpisodeTextImportCommands): Record<EpisodeTextImportCommandKey, TEpisodeTextImportCommandValue> {
    return Object.values(EpisodeTextImportCommandKey)
      .reduce((acc, key) => {
        acc[key as EpisodeTextImportCommandKey] = commands.get(key as EpisodeTextImportCommandKey) ?? new Map();
        return acc;
      }, {} as Record<EpisodeTextImportCommandKey, TEpisodeTextImportCommandValue>);
  }

  public static getCheckEntityTypes(importCommands: TEpisodeTextImportCommands): 'single' | 'multi' | 'conditionList' | 'playerHas' | 'none' {
    const commands = EpisodeTextImportHelper.convertCommandsToRecordType(importCommands);
    const operationName = commands.check.values().next().value?.[2]?.toLocaleLowerCase().trim() ?? '';

    const isSingleCheck = operationName?.startsWith('if');
    if (isSingleCheck) {
      return 'single';
    }
    const isMultiCheck = ['min', 'max', 'lowest', 'highest'].includes(operationName);
    if (isMultiCheck) {
      return 'multi';
    }
    const isConditionList = operationName === 'conditions';
    if (isConditionList) {
      return 'conditionList';
    }
    const isPlayerHas = operationName === 'playerhas';
    if (isPlayerHas) {
      return 'playerHas';
    }
    return 'none';
  }

  public static getSingleCheckCommandValues(importCommands: TEpisodeTextImportCommands) {
    const commands = EpisodeTextImportHelper.convertCommandsToRecordType(importCommands);
    const singleCheckTypeMemoryNameRaw = commands.check.values().next().value?.[3]?.toLocaleLowerCase().trim() ?? '';
    const singleCheckTypeMemoryName = stripCharacterMemoryPrefix(singleCheckTypeMemoryNameRaw);
    const singleCheckTypeOperator = commands.check.values().next().value?.[4]?.toLocaleLowerCase().trim() ?? '';
    const singleCheckTypeTextOperator = commands.check.values().next().value?.[5]?.toLocaleLowerCase().trim() ?? '';
    const singleCheckTypeValue = commands.check.values().next().value?.[6]?.trim().replace(/["']/g, '') ?? '';
    const singleCheckTypeTrueGoto = commands.check.values().next().value?.[8]?.toLocaleLowerCase().trim() ?? '';
    const singleCheckTypeFalseGoto = commands.check.values().next().value?.[10]?.toLocaleLowerCase().trim() ?? '';
    return {
      singleCheckTypeMemoryName,
      singleCheckTypeOperator,
      singleCheckTypeTextOperator,
      singleCheckTypeValue,
      singleCheckTypeTrueGoto,
      singleCheckTypeFalseGoto,
    };
  }

  public static getPlayerHasCheckCommandValues(importCommands: TEpisodeTextImportCommands) {
    const commands = EpisodeTextImportHelper.convertCommandsToRecordType(importCommands);
    const matchType = commands.check.values().next().value?.[3]?.toLocaleLowerCase().trim() ?? 'all';
    const rewardNamesRaw = commands.check.values().next().value?.[4]?.trim() ?? '';
    const rewardNames = rewardNamesRaw.split(',').map((el: string) => el.trim()).filter(Boolean);
    const trueGoto = commands.check.values().next().value?.[6]?.toLocaleLowerCase().trim() ?? '';
    const falseGoto = commands.check.values().next().value?.[8]?.toLocaleLowerCase().trim() ?? '';
    return {
      matchType,
      rewardNames,
      trueGoto,
      falseGoto,
    };
  }

  public static getMultiCheckCommandValues(importCommands: TEpisodeTextImportCommands) {
    const commands = EpisodeTextImportHelper.convertCommandsToRecordType(importCommands);
    const multiCheckTypeFuncName = commands.check.values().next().value?.[2]?.toLocaleLowerCase().trim() ?? '';
    const multiCheckTypeMemoryTextNames = commands.check.values().next().value?.[5]?.toLocaleLowerCase().trim() ?? '';
    const multiCheckTypeGotoTextValues = commands.check.values().next().value?.[6]?.toLocaleLowerCase().trim() ?? '';

    const multiCheckTypeMemoryNames = multiCheckTypeMemoryTextNames.split(',')
      .map((el: string) => el.toLocaleLowerCase().trim());

    const gotoValues = multiCheckTypeGotoTextValues.split(',')
      .map((el: string) => {
        const match = el.match(/if\s+(\w*)\s+(?:goto\s+)?(\w*)/i);
        return {
          memory: match?.[1] ?? '',
          gotoNode: match?.[2] ?? '',
        };
      });
    const gotoValuesSorted = sortBy(gotoValues, (el) => multiCheckTypeMemoryNames.indexOf(el.memory));
    const multiCheckTypeGotoValues: string[] = gotoValuesSorted.map((el) => el.gotoNode) ?? [];
    const multiCheckTypeSwitchMemoryNames: string[] = gotoValuesSorted.map((el) => el.memory.toLocaleLowerCase().trim());

    return {
      multiCheckTypeFuncName,
      multiCheckTypeMemoryTextNames,
      multiCheckTypeGotoTextValues,
      multiCheckTypeMemoryNames,
      multiCheckTypeGotoValues,
      multiCheckTypeSwitchMemoryNames,
    };
  }

  public static getConditionListCheckCommandValues(importCommands: TEpisodeTextImportCommands) {
    const commands = EpisodeTextImportHelper.convertCommandsToRecordType(importCommands);
    const conditionListMemoryNameRaw = commands.check.values().next().value?.[3]?.toLocaleLowerCase().trim() ?? '';
    const conditionListMemoryName = stripCharacterMemoryPrefix(conditionListMemoryNameRaw);

    return {
      conditionListMemoryName,
    };
  }

  public static getInvalidValidCommand(commandString: string): string | undefined {
    const commandMatch = commandString.match(COMMAND_KEY_PATTERN);
    if (!commandMatch) {
      return undefined;
    }
    for (let i = 0; i < commandMatch.length; i++) {
      const commandType = Object.values(EpisodeTextImportCommands).flat();
      if (!commandType.some((command) => command.pattern.test(commandMatch[i]))) {
        return commandMatch[i];
      }
    }
    return undefined;
  }
}
